import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TRANSFORMERS, $convertToMarkdownString, $convertFromMarkdownString } from "@lexical/markdown";
import styled from "styled-components";
import Label from "~/components-shared/Label";
import ActionsPlugin from "~/components-shared/InputMarkdownTextArea/plugins/ActionsPlugin";
import ClickableLinkPlugin from "~/components-shared/InputMarkdownTextArea/plugins/ClickableLinkPlugin";
import OnUpdatePlugin from "~/components-shared/InputMarkdownTextArea/plugins/OnUpdatePlugin";
import { lexicalCss } from "~/components/GlobalStyle/css/lexical.css";
import { genericColor } from "~/lib/resolvers";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import SpeechToTextPlugin from "./plugins/SpeechToTextPlugin";
const Wrapper = styled.div`
  ${lexicalCss};
  width: 100%;
  ${_ref => {
  let {
    $s,
    theme
  } = _ref;
  return theme.spacing.resolve(theme, $s);
}};

  .editor-inner,
  .toolbar {
    background-color: ${_ref2 => {
  let {
    $color,
    theme
  } = _ref2;
  return genericColor(theme, $color);
}} !important;
  }
`;
const Error = styled.span`
  font-size: 0.8rem;
  display: inline-block;
  margin-left: 0.5rem;
  color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.danger.main;
}};
`;
const Placeholder = styled.span`
  position: absolute;
  top: 15px;
  left: 10px;
  color: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.text.secondary;
}};
`;
const InputMarkdownTextArea = _ref5 => {
  let {
    name,
    value,
    onChange,
    error,
    label,
    required = false,
    disabled = false,
    color = "translucent",
    placeholder,
    s,
    labelProps,
    children,
    ...rest
  } = _ref5;
  const handleChange = editorState => {
    editorState.read(() => {
      if (typeof onChange === "function") {
        onChange($convertToMarkdownString());
      }
    });
  };
  const initialConfig = {
    namespace: name,
    theme: {
      heading: {
        h1: "lexical__h1",
        h2: "lexical__h2",
        h3: "lexical__h3",
        h4: "lexical__h4",
        h5: "lexical__h5",
        h6: "lexical__h6"
      },
      code: "lexical__code",
      paragraph: "lexical__paragraph",
      quote: "lexical__quote",
      hashtag: "lexical__hashtag",
      text: {
        bold: "lexical__textBold",
        code: "lexical__textCode",
        italic: "lexical__textItalic",
        strikethrough: "lexical__textStrikethrough",
        subscript: "lexical__textSubscript",
        superscript: "lexical__textSuperscript",
        underline: "lexical__textUnderline",
        underlineStrikethrough: "lexical__textUnderlineStrikethrough"
      }
    },
    nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, TableNode, TableCellNode, TableRowNode, AutoLinkNode, LinkNode],
    editable: !disabled,
    editorState: () => $convertFromMarkdownString(value || "", TRANSFORMERS),
    onError: () => false
  };
  return /*#__PURE__*/React.createElement(Wrapper, {
    className: "lexical",
    $color: color,
    $s: s
  }, !!label && /*#__PURE__*/React.createElement(Label, _extends({
    required: required,
    s: {
      mb: 2
    }
  }, labelProps), label), /*#__PURE__*/React.createElement(LexicalComposer, {
    initialConfig: initialConfig
  }, /*#__PURE__*/React.createElement("div", {
    className: "editor-container"
  }, /*#__PURE__*/React.createElement(ToolbarPlugin, null), /*#__PURE__*/React.createElement("div", {
    className: "editor-inner"
  }, /*#__PURE__*/React.createElement(RichTextPlugin, {
    contentEditable: /*#__PURE__*/React.createElement(ContentEditable, _extends({
      className: "editor-input"
    }, rest)),
    placeholder: /*#__PURE__*/React.createElement(Placeholder, null, placeholder),
    ErrorBoundary: LexicalErrorBoundary
  }), /*#__PURE__*/React.createElement(ListPlugin, null), /*#__PURE__*/React.createElement(LinkPlugin, null), /*#__PURE__*/React.createElement(ActionsPlugin, null), /*#__PURE__*/React.createElement(SpeechToTextPlugin, null), /*#__PURE__*/React.createElement(MarkdownShortcutPlugin, {
    transformers: TRANSFORMERS
  }), /*#__PURE__*/React.createElement(ClickableLinkPlugin, null)), /*#__PURE__*/React.createElement(OnChangePlugin, {
    onChange: handleChange
  }), /*#__PURE__*/React.createElement(HistoryPlugin, null)), /*#__PURE__*/React.createElement(OnUpdatePlugin, {
    value: value
  })), error && /*#__PURE__*/React.createElement(Error, null, error));
};
export default InputMarkdownTextArea;